/**
 * CalendarService.ts
 *
 * 캘린더 애플리케이션의 메인 서비스 모듈
 *
 * 이 파일은 모든 캘린더 관련 모듈들을 통합하는 진입점 역할을 합니다.
 * 기존 API를 유지하면서 내부적으로 모듈화된 구조를 사용합니다.
 *
 * 통합된 모듈들:
 * - CalendarState: 전역 상태 및 이벤트 관리
 * - CalendarClient: CalDAV 클라이언트 관리
 * - CalendarSync: 동기화 관련 기능
 * - EventManager: 이벤트 CRUD 작업
 * - EventInstanceManager: 이벤트 인스턴스 관리
 * - CalendarManager: 캘린더 관리
 * - NotificationService: 알림 관리
 *
 * 이 모듈을 통해 다른 컴포넌트들은 단일 인터페이스로
 * 모든 캘린더 기능에 접근할 수 있습니다.
 */

// Import fetchFreeBusy for internal use in fetchFreeBusyInfo
import { fetchFreeBusy } from './EventManager'
import type { BusyItem, FreeBusyInfo } from '../types/BusyTime'

// CalendarState에서 상태 및 이벤트 관리 함수들 export
export {
  // 상태 관리
  getCurrentDate,
  setCurrentDate,
  getUserInfo,
  getCalendars,
  getDefaultCalendar,
  hasSharedCalendars,
  getInstances,
  getInstance,
  getNotifications,
  isSyncing,

  // 동기화 상태 수정 함수들
  setIsFetchingCalendars,
  setFetchingEventQueue,
  setLatestTimeRange,
  setDoingFetchNotifications,

  // 이벤트 리스너 관리
  addNotificationListener,
  removeNotificationListener,
  addFolderListener,
  removeFolderListener,
  addInstanceListener,
  removeInstanceListener,
  addSyncListener,
  removeSyncListener,

  // 로그아웃
  logout,

  // 클라이언트 초기화
  initClient,
} from './CalendarState'

// CalendarSync에서 동기화 관련 함수들 export
export { fetchCalendars, reloadEvents, enableSync } from './CalendarSync'

// EventManager에서 이벤트 관련 함수들 export
export {
  EventDeleteType,
  createEvent,
  updateEvent,
  updateEventChangeTime,
  deleteEvent,
  deleteEventByUid,
  meetingResponse,
  meetingResponseByUid,
  searchEventByUid,
  moveEvent,
  findCalendarByEventServerId,
  addAttachment,
  deleteAttachment,
  fetchAttachment,
  batchUpdateAttachments,
  searchEventByKeyword,
  fetchFreeBusy,
} from './EventManager'

// CalendarManager에서 캘린더 관리 함수들 export
export {
  createCalendar,
  updateCalendar,
  deleteCalendar,
  getCalendarInfo,
  sendInviteCalendar,
  deleteInviteCalendar,
  setViewType,
} from './CalendarManager'

// NotificationService에서 알림 관련 함수들 export
export { fetchNotifications, responseNotification, deleteNotification } from './NotificationService'

// EventInstanceManager에서 인스턴스 관련 함수들 export
export { findInstanceByUid } from './EventInstanceManager'

// SubscriptionManager에서 ICS 구독 관련 함수들 export
export {
  subscribe,
  unsubscribe,
  updateSubscription,
  refreshSubscription,
  refreshAllSubscriptions,
  getSubscriptions,
  initSubscriptions,
  stopAutoRefresh,
} from './subscription'

export type { ICSSubscription, RefreshInterval } from './subscription'

/**
 * Fetch free/busy information for attendees
 *
 * Uses CalDAV VFREEBUSY query via the existing fetchFreeBusy implementation.
 * Reference: RFC 4791 - CalDAV, RFC 6638 - CalDAV Scheduling Extensions
 *
 * @param attendeeEmails - Email addresses of attendees
 * @param startDate - Query start date
 * @param endDate - Query end date
 * @returns Map of email to busy times
 *
 * Response structure:
 * {
 *   "user@example.com": {
 *     busy: [{ start: Date, end: Date, isAllday: boolean }],
 *     tentative: [{ start: Date, end: Date, isAllday: boolean }],
 *     outOfOffice: [{ start: Date, end: Date, isAllday: boolean }],
 *     noData: boolean
 *   }
 * }
 *
 * Note: Currently all busy times are returned in the 'busy' array.
 * TODO: Parse FBTYPE parameter from VFREEBUSY response to categorize into
 * busy/tentative/outOfOffice based on RFC 5545 FBTYPE values:
 * - BUSY (default)
 * - BUSY-TENTATIVE
 * - BUSY-UNAVAILABLE (out of office)
 */
export const fetchFreeBusyInfo = async (
  attendeeEmails: string[],
  startDate: Date,
  endDate: Date,
): Promise<FreeBusyInfo> => {
  return new Promise((resolve) => {
    fetchFreeBusy(
      attendeeEmails,
      startDate,
      endDate,
      (freeBusyResponses: Record<string, BusyItem[] | null | undefined>) => {
        // Transform the response from array format to categorized structure
        const result: FreeBusyInfo = {}

        attendeeEmails.forEach((email) => {
          const busyTimes = freeBusyResponses[email]

          // Query failed - data unavailable
          if (busyTimes == null) {
            result[email] = {
              busy: [],
              tentative: [],
              outOfOffice: [],
              noData: false,
              unknown: true,
            }
          } else {
            // Query succeeded
            result[email] = {
              // Currently all entries go into 'busy' since FBTYPE parsing not implemented
              busy: busyTimes.map((block) => ({
                start: block.start,
                end: block.end,
                isAllday: block.isAllday || false,
              })),
              tentative: [],
              outOfOffice: [],
              noData: busyTimes.length === 0,
              unknown: false,
            }
          }
        })

        resolve(result)
      },
    )
  })
}
